// ** React Imports

import { useEffect, useState } from 'react'
import FormTile from 'src/components/common/containers/FormTile'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import useSWR, { mutate } from 'swr'
import { apiURI } from 'src/lib/utils/constants'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { patchRequest, postRequest, putRequest } from 'src/lib/core/api-client'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import EarnRewards from 'src/components/rewards/setup/earn/EarnRewards'
import { isEmptyObj } from 'src/lib/utils/helpers'
import { EarnBurnRewardsPayloadType, ToggleRewardsType } from 'src/lib/types/rewards'
import BurnRewards from 'src/components/rewards/setup/burn/BurnRewards'
import CustomerDetails from 'src/components/rewards/setup/customer-details/CustomerDetails'
import EnableRewards from 'src/components/rewards/setup/EnableRewards'
import DisableRewards from 'src/components/rewards/setup/DisableRewards'
import { PaymentMethodConfigType } from 'src/lib/types/customizeUI'
import { parsePaymentModesResponse } from 'src/lib/utils/customizeUIHelpers'
import { parsePaymentModeForRewardsConfig } from 'src/lib/utils/integrationsHelpers'
import { RewardsProvider } from 'src/lib/types/integrations'
import { useRewardsPermissions } from 'src/lib/access-permission-hooks/useRewardsPermissions'
import useGTM from 'src/lib/hooks/useGTM'
const ISSUER_ID = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

const RewardsSetupTab = () => {
  const {
    getters: { merchant }
  } = useMerchant()
  const { setLoaderProperties } = useLoader()
  const { canEnableDisableRewards } = useRewardsPermissions()
  const { sendGTMTrigger } = useGTM()
  const {
    data: rewardsResponse,
    error: rewardsResponseError,
    isValidating
  } = useSWR(
    Boolean(merchant?.accountId) && Boolean(ISSUER_ID)
      ? [`/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`, apiURI.REWARDS_ENGINE]
      : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [isRewardsConfigured, setIsRewardsConfigured] = useState<boolean>(false)
  const [openDeactivateConfirmDialog, setOpenDeactivateConfirmDialog] = useState<boolean>(false)
  const [rewardsActive, setRewardsActive] = useState<boolean>(false)

  useEffect(() => {
    if (!Boolean(rewardsResponse) || Boolean(isValidating)) return
    const isShopfloAndActive =
      (rewardsResponse as any)?.data?.provider === 'SHOPFLO' && (rewardsResponse as any)?.data?.active === true
    setRewardsActive(isShopfloAndActive)
    setIsRewardsConfigured(true)
  }, [rewardsResponse, isValidating])

  useEffect(() => {
    if (!rewardsResponseError || isValidating) return
    if ((rewardsResponseError as any)?.response?.status === 404) {
      setIsRewardsConfigured(false)
      setRewardsActive(false)
    }
  }, [isValidating, rewardsResponseError])

  // ** Alert Handlers ** //
  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: any, reason: string) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  const handleUpdateRewardConfig = async (payload: EarnBurnRewardsPayloadType | ToggleRewardsType) => {
    if (isEmptyObj(payload)) return
    setLoaderProperties({ isLoaderVisible: true, message: 'Updating rewards configuration' })
    const updatedPayload = {
      ...payload,
      provider: 'SHOPFLO'
    }
    try {
      if (isRewardsConfigured) {
        const paymentModePayload = parsePaymentModeForRewardsConfig(
          RewardsProvider.SHOPFLO,
          (payload as ToggleRewardsType).active
        )
        const promises = [
          patchRequest(
            `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`,
            apiURI.REWARDS_ENGINE,
            updatedPayload
          )
        ]
        if ((payload as ToggleRewardsType).active) {
          promises.push(
            putRequest(`/fms/v1/merchant-gateways/reorder-payment-modes`, apiURI.FLO_CHECKOUT, paymentModePayload)
          )
        }
        const [rewardsResponse, paymentModesResponse] = await Promise.all(promises)
        if (paymentModesResponse) {
          mutate('/fms/v1/merchant-payment-modes')
        }
      } else {
        await postRequest(
          `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`,
          apiURI.REWARDS_ENGINE,
          updatedPayload
        )
        setIsRewardsConfigured(true)
      }
      mutate([`/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`, apiURI.REWARDS_ENGINE])
      handleAlerts('success', 'Rules saved successfully.')
    } catch (e) {
      console.error(e)
      handleAlerts('error', 'Something went wrong')
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleToggleRewards = async (requestActivate: boolean) => {
    if (!Boolean(isRewardsConfigured)) {
      setRewardsActive(true)
      return
    }
    const payload = {
      active: Boolean(requestActivate),
      provider: 'SHOPFLO'
    }
    try {
      await patchRequest(
        `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`,
        apiURI.REWARDS_ENGINE,
        payload
      )
      sendGTMTrigger('rewards_toggled', 'toggle', {
        toggleStatus: requestActivate ? 'enabled' : 'disabled'
      })

      if (requestActivate) {
        setRewardsActive(true)
        setIsRewardsConfigured(true)
        handleAlerts('success', 'Rewards activated successfully.')
        return
      }
      setOpenDeactivateConfirmDialog(false)
      setRewardsActive(false)
      handleAlerts('success', 'Rewards deactivated successfully.')
    } catch (e) {
      console.error(e)
      handleAlerts('error', 'Something went wrong')
    }
  }

  if (Boolean(isValidating)) return <></>

  return (
    <div className='flex flex-wrap w-full gap-4 '>
      <div className='flex flex-col w-full gap-4 lg:w-3/5'>
        {!Boolean(rewardsActive) && <EnableRewards handleUpdateRewardConfig={handleUpdateRewardConfig} />}

        {Boolean(rewardsResponse) && (
          <>
            <EarnRewards
              handleUpdateRewardConfig={handleUpdateRewardConfig}
              rewardsConfig={(rewardsResponse as any).data}
            />
            <BurnRewards
              handleUpdateRewardConfig={handleUpdateRewardConfig}
              rewardsConfig={(rewardsResponse as any).data}
            />
          </>
        )}
        <CustomerDetails />
        {canEnableDisableRewards && (
          <FormTile title={'Settings'} tooltip={''}>
            {Boolean(rewardsActive) ? (
              <DisableRewards handleUpdateRewardConfig={handleUpdateRewardConfig} />
            ) : (
              <>
                <EnableRewards handleUpdateRewardConfig={handleUpdateRewardConfig} />
              </>
            )}
          </FormTile>
        )}
      </div>

      {openDeactivateConfirmDialog ? (
        <ConfirmationDialog
          jobButton={'De-activate'}
          cancelButton={'Cancel'}
          jobButtonColor='error'
          cancelButtonColor='secondary'
          confirmationText={'Customers will not be able to earn or redeem any credits.'}
          referrer={'rewards-deactivate'}
          title={'Are you sure you want to de-activate Rewards?'}
          isOpen={openDeactivateConfirmDialog}
          setIsConfirmationOpen={setOpenDeactivateConfirmDialog}
          handleJob={async () => {
            handleToggleRewards(false)
          }}
        />
      ) : (
        <></>
      )}

      {/* Alerts */}
      {openAlert.length ? (
        <Alerts
          open={Boolean(openAlert.length)}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : (
        <></>
      )}
    </div>
  )
}

export default RewardsSetupTab
